import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Sparkles, ArrowUpCircle, ChevronRight, RefreshCw, FlaskConical, RotateCcw, Truck, Calculator, AlertTriangle, Settings, Save } from 'lucide-react';
import { PrintProvidersDialog } from './PrintProvidersDialog';
import { ShippingProvidersDialog } from './ShippingProvidersDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtUsd = (v) => `$${(Number(v) || 0).toFixed(2)}`;
const fmtEur = (v) => `€${(Number(v) || 0).toFixed(2)}`;
const usdToEur = (v) => (Number(v) || 0) * 0.92;
const eurToUsd = (v) => (Number(v) || 0) / 0.92;
const fmtEurFromUsd = (v) => fmtEur(usdToEur(v));

export default function AdminCostsPanel() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [topUpDialog, setTopUpDialog] = useState(null);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpNote, setTopUpNote] = useState('');
  const [toppingUp, setToppingUp] = useState(false);
  const [simulateDialog, setSimulateDialog] = useState(null);
  const [simulateAmount, setSimulateAmount] = useState('');
  const [simulateNote, setSimulateNote] = useState('');
  const [simulating, setSimulating] = useState(false);
  // Project costs (AI + print + shipping + margin) — calculated per book
  // with the chosen print provider and qty.
  const [providers, setProviders] = useState([]);
  const [providersDialogOpen, setProvidersDialogOpen] = useState(false);
  const [shippingProviders, setShippingProviders] = useState([]);
  const [shippingProvidersDialogOpen, setShippingProvidersDialogOpen] = useState(false);
  const [projectCostDialog, setProjectCostDialog] = useState(null); // selected book
  const [projectQty, setProjectQty] = useState(1);
  const [projectProviderId, setProjectProviderId] = useState('');
  const [projectBreakdown, setProjectBreakdown] = useState(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [assigningProvider, setAssigningProvider] = useState(false);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const [overviewRes, providersRes, shippingRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/llm-usage`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/print-providers`, { withCredentials: true })
          .catch(() => ({ data: { providers: [] } })),
        axios.get(`${API_URL}/api/admin/shipping-providers`, { withCredentials: true })
          .catch(() => ({ data: { providers: [] } })),
      ]);
      setOverview(overviewRes.data);
      setProviders(providersRes.data?.providers || []);
      setShippingProviders(shippingRes.data?.providers || []);
    } catch (err) {
      toast.error('No se pudo cargar el panel de costes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOverview(); }, []);

  // ---- Project cost calculator ----
  const openProjectCost = async (book) => {
    const initialProviderId =
      providers.find((p) => p.provider_id === book?.print_provider_id)?.provider_id
      || providers.find((p) => p.is_default && p.is_active)?.provider_id
      || providers[0]?.provider_id
      || '';
    setProjectCostDialog(book);
    setProjectQty(1);
    setProjectProviderId(initialProviderId);
    setProjectBreakdown(null);
    if (initialProviderId) {
      await loadProjectBreakdown(book.book_id, 1, initialProviderId);
    }
  };

  const loadProjectBreakdown = async (bookId, qty, providerId) => {
    if (!bookId || !providerId || !qty) return;
    setProjectLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/books/${bookId}/project-cost`,
        { params: { qty, provider_id: providerId }, withCredentials: true }
      );
      setProjectBreakdown(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo calcular el coste del proyecto');
    } finally {
      setProjectLoading(false);
    }
  };

  // Recalculate when qty or provider changes
  useEffect(() => {
    if (projectCostDialog && projectProviderId && projectQty) {
      loadProjectBreakdown(projectCostDialog.book_id, projectQty, projectProviderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectQty, projectProviderId]);

  const assignProviderToBook = async () => {
    if (!projectCostDialog || !projectProviderId) return;
    setAssigningProvider(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/books/${projectCostDialog.book_id}/print-provider`,
        { provider_id: projectProviderId },
        { withCredentials: true }
      );
      toast.success('Proveedor asignado al libro');
      // Update local row so the UI stays in sync until next fetch
      setOverview((prev) => prev ? ({
        ...prev,
        books: prev.books.map((b) =>
          b.book_id === projectCostDialog.book_id
            ? { ...b, print_provider_id: projectProviderId }
            : b
        ),
      }) : prev);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo asignar el proveedor');
    } finally {
      setAssigningProvider(false);
    }
  };

  const openDrilldown = async (book) => {
    setDrilldown({ book, summary: null });
    setDrilldownLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/books/${book.book_id}/cost-summary`,
        { withCredentials: true }
      );
      setDrilldown({ book, summary: res.data });
    } catch {
      toast.error('No se pudo cargar el detalle');
      setDrilldown(null);
    } finally {
      setDrilldownLoading(false);
    }
  };

  const submitTopUp = async () => {
    const amountEur = parseFloat(topUpAmount);
    if (!amountEur || amountEur <= 0) {
      toast.error('Introduce un importe positivo en EUR');
      return;
    }
    const amountUsd = eurToUsd(amountEur);
    setToppingUp(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/books/${topUpDialog.book_id}/budget/top-up`,
        { amount_usd: amountUsd, note: topUpNote },
        { withCredentials: true }
      );
      toast.success(`Saldo recargado: +€${amountEur.toFixed(2)} para "${topUpDialog.title}"`);
      setTopUpDialog(null);
      setTopUpAmount('');
      setTopUpNote('');
      fetchOverview();
      if (drilldown?.book?.book_id === topUpDialog.book_id) {
        openDrilldown(drilldown.book);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en la recarga');
    } finally {
      setToppingUp(false);
    }
  };

  const submitSimulation = async () => {
    const amountEur = parseFloat(simulateAmount);
    if (!amountEur || amountEur <= 0) {
      toast.error('Introduce un importe positivo');
      return;
    }
    const amountUsd = eurToUsd(amountEur);
    setSimulating(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/books/${simulateDialog.book_id}/simulate-cost`,
        { amount_usd: amountUsd, note: simulateNote || 'Simulación de consumo' },
        { withCredentials: true }
      );
      toast.success(`Simulado consumo de €${amountEur.toFixed(2)} en "${simulateDialog.title}"`);
      setSimulateDialog(null);
      setSimulateAmount('');
      setSimulateNote('');
      fetchOverview();
      if (drilldown?.book?.book_id === simulateDialog.book_id) {
        openDrilldown(drilldown.book);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en la simulación');
    } finally {
      setSimulating(false);
    }
  };

  const resetSpent = async (book) => {
    if (!window.confirm(`¿Resetear a $0 el consumo simulado de "${book.title}"?\n\nEsto NO afecta a los registros históricos en llm_usage.`)) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/books/${book.book_id}/reset-spent`,
        { confirm: true },
        { withCredentials: true }
      );
      toast.success('Consumo reseteado a $0');
      fetchOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al resetear');
    }
  };

  if (loading) {
    return (
      <Card className="card-elevated">
        <CardContent className="py-12 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!overview) return null;

  return (
    <div className="space-y-4" data-testid="admin-costs-panel">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card className="card-elevated">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Gasto IA total</p>
            <p className="text-2xl font-bold">{fmtEur(overview.total_spent_eur)}</p>
            <p className="text-[11px] text-muted-foreground mt-1">≈ {fmtUsd(overview.total_spent_usd)}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Libros con consumo</p>
            <p className="text-2xl font-bold">{overview.books.length}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Budget por defecto</p>
            <p className="text-2xl font-bold">{fmtEur(usdToEur(overview.default_budget_usd))}</p>
            <p className="text-[11px] text-muted-foreground mt-1">≈ {fmtUsd(overview.default_budget_usd)}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Proveedores</p>
              <p className="text-sm font-medium">{providers.length} imprentas</p>
              <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                Activa: {providers.find((p) => p.is_default)?.name || '—'}
              </p>
            </div>
            <Button
              onClick={() => setProvidersDialogOpen(true)}
              size="sm" variant="outline"
              className="gap-1 text-xs flex-shrink-0"
              data-testid="manage-providers-btn"
            >
              <Settings className="w-3.5 h-3.5" /> Gestionar
            </Button>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Transportistas</p>
              <p className="text-sm font-medium">{shippingProviders.length} {shippingProviders.length === 1 ? 'transportista' : 'transportistas'}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                Activo: {shippingProviders.find((p) => p.is_default)?.name || '—'}
              </p>
            </div>
            <Button
              onClick={() => setShippingProvidersDialogOpen(true)}
              size="sm" variant="outline"
              className="gap-1 text-xs flex-shrink-0"
              data-testid="manage-shipping-providers-btn"
            >
              <Settings className="w-3.5 h-3.5" /> Gestionar
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Books table */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Consumo por libro
          </CardTitle>
          <CardDescription>
            Coste estimado en USD; conversión a EUR informativa al cambio del 0.92. Pulsa una fila para ver detalle.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {overview.books.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Aún no hay libros creados.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="costs-table">
                <thead className="text-left border-b">
                  <tr>
                    <th className="p-2 font-medium text-xs uppercase tracking-wide text-muted-foreground">Libro</th>
                    <th className="p-2 font-medium text-xs uppercase tracking-wide text-muted-foreground">Cliente</th>
                    <th className="p-2 font-medium text-xs uppercase tracking-wide text-muted-foreground text-right">Llamadas</th>
                    <th className="p-2 font-medium text-xs uppercase tracking-wide text-muted-foreground text-right">Gasto</th>
                    <th className="p-2 font-medium text-xs uppercase tracking-wide text-muted-foreground text-right">Budget</th>
                    <th className="p-2 font-medium text-xs uppercase tracking-wide text-muted-foreground text-right">% libre</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {overview.books.map((b) => {
                    const isLow = b.remaining_pct < 25;
                    const isDepleted = b.remaining_pct <= 0;
                    return (
                      <tr
                        key={b.book_id}
                        className="border-b hover:bg-muted/40 transition-colors cursor-pointer"
                        onClick={() => openDrilldown(b)}
                        data-testid={`cost-row-${b.book_id}`}
                      >
                        <td className="p-2">
                          <p className="font-medium">{b.title || '(sin título)'}</p>
                          <p className="text-[11px] text-muted-foreground">{b.book_id}</p>
                        </td>
                        <td className="p-2">
                          <p className="text-xs">{b.owner_name || '—'}</p>
                          <p className="text-[11px] text-muted-foreground">{b.owner_email || ''}</p>
                        </td>
                        <td className="p-2 text-right tabular-nums">{b.call_count}</td>
                        <td className="p-2 text-right tabular-nums">
                          <p>{fmtEur(b.spent_eur)}</p>
                          <p className="text-[11px] text-muted-foreground">{fmtUsd(b.spent_usd)}</p>
                        </td>
                        <td className="p-2 text-right tabular-nums">
                          <p>{fmtEur(usdToEur(b.budget_usd))}</p>
                          <p className="text-[11px] text-muted-foreground">{fmtUsd(b.budget_usd)}</p>
                        </td>
                        <td className="p-2 text-right">
                          <Badge className={
                            isDepleted ? 'bg-red-100 text-red-700' :
                            isLow ? 'bg-amber-100 text-amber-700' :
                            'bg-green-100 text-green-700'
                          }>
                            {b.remaining_pct}%
                          </Badge>
                        </td>
                        <td className="p-2">
                          <div className="flex items-center gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); openProjectCost(b); }}
                              className="gap-1 text-xs text-teal-700 hover:text-teal-900 hover:bg-teal-50"
                              data-testid={`project-cost-btn-${b.book_id}`}
                              title="Calcular coste y margen del proyecto (IA + impresión + envío)"
                            >
                              <Calculator className="w-3.5 h-3.5" />
                              Coste proyecto
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); setSimulateDialog(b); }}
                              className="gap-1 text-xs text-amber-700 hover:text-amber-900 hover:bg-amber-50"
                              data-testid={`simulate-btn-${b.book_id}`}
                              title="Simular consumo de créditos para probar UI del promotor"
                            >
                              <FlaskConical className="w-3.5 h-3.5" />
                              Simular
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); resetSpent(b); }}
                              className="gap-1 text-xs text-muted-foreground hover:text-foreground"
                              data-testid={`reset-btn-${b.book_id}`}
                              title="Resetear consumo a 0"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); setTopUpDialog(b); }}
                              className="gap-1 text-xs"
                              data-testid={`topup-btn-${b.book_id}`}
                            >
                              <ArrowUpCircle className="w-3.5 h-3.5" />
                              Recargar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drilldown dialog */}
      <Dialog open={!!drilldown} onOpenChange={(o) => !o && setDrilldown(null)}>
        <DialogContent className="max-w-2xl" data-testid="drilldown-dialog">
          <DialogHeader>
            <DialogTitle>Desglose de consumo IA</DialogTitle>
          </DialogHeader>
          {drilldownLoading || !drilldown?.summary ? (
            <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-md border p-3">
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Gastado</p>
                  <p className="text-xl font-bold">{fmtEur(drilldown.summary.spent_eur)}</p>
                  <p className="text-[11px] text-muted-foreground">{fmtUsd(drilldown.summary.spent_usd)}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Budget</p>
                  <p className="text-xl font-bold">{fmtEur(drilldown.summary.budget_eur)}</p>
                  <p className="text-[11px] text-muted-foreground">{fmtUsd(drilldown.summary.budget_usd)}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Libre</p>
                  <p className="text-xl font-bold">{fmtEur(usdToEur(drilldown.summary.remaining_usd))}</p>
                  <p className="text-[11px] text-muted-foreground">{fmtUsd(drilldown.summary.remaining_usd)}</p>
                </div>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Por propósito</p>
                <table className="w-full text-xs">
                  <tbody>
                    {drilldown.summary.by_purpose.map((p) => (
                      <tr key={p.purpose} className="border-b">
                        <td className="py-1">{p.purpose}</td>
                        <td className="py-1 text-right tabular-nums text-muted-foreground">{p.calls} llamadas</td>
                        <td className="py-1 text-right tabular-nums">{fmtEurFromUsd(p.cost_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Por modelo</p>
                <table className="w-full text-xs">
                  <tbody>
                    {drilldown.summary.by_model.map((p) => (
                      <tr key={p.model} className="border-b">
                        <td className="py-1 font-mono text-[10px]">{p.model}</td>
                        <td className="py-1 text-right tabular-nums text-muted-foreground">{p.calls} llamadas</td>
                        <td className="py-1 text-right tabular-nums">{fmtEurFromUsd(p.cost_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Últimas 25 llamadas</p>
                <div className="max-h-48 overflow-y-auto border rounded-md">
                  <table className="w-full text-[11px]">
                    <thead className="sticky top-0 bg-muted/50 text-left">
                      <tr>
                        <th className="p-1.5">Fecha</th>
                        <th className="p-1.5">Propósito</th>
                        <th className="p-1.5 text-right">Tokens (in/out)</th>
                        <th className="p-1.5 text-right">Coste</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drilldown.summary.recent_calls.map((c) => (
                        <tr key={c.id} className="border-b">
                          <td className="p-1.5 text-muted-foreground">
                            {c.timestamp ? new Date(c.timestamp).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                          </td>
                          <td className="p-1.5">{c.purpose}</td>
                          <td className="p-1.5 text-right tabular-nums text-muted-foreground">{c.prompt_tokens}/{c.completion_tokens}</td>
                          <td className="p-1.5 text-right tabular-nums">{fmtEurFromUsd(c.cost_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Simulate dialog */}
      <Dialog open={!!simulateDialog} onOpenChange={(o) => !o && setSimulateDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="simulate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-amber-600" />
              Simular consumo de créditos
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
              <p className="text-xs text-amber-900 leading-relaxed">
                Esto añade consumo <strong>simulado</strong> al libro para probar cómo
                cambia la UI del promotor según el saldo (morado &gt;25% → ámbar &lt;25% → rojo agotado).
                No es una llamada real a IA.
              </p>
            </div>
            <p className="text-sm">
              Libro: <strong>{simulateDialog?.title}</strong><br/>
              Estado: <strong>{simulateDialog?.remaining_pct}%</strong> libre · gastado {fmtEur(usdToEur(simulateDialog?.spent_usd || 0))}
            </p>
            <div>
              <Label className="text-xs">Importe a simular (EUR) <span className="text-red-500">*</span></Label>
              <Input
                type="number"
                step="0.5"
                min="0.01"
                value={simulateAmount}
                onChange={(e) => setSimulateAmount(e.target.value)}
                placeholder="Ej.: 4"
                className="mt-1"
                data-testid="simulate-amount"
              />
              <div className="mt-2 grid grid-cols-4 gap-1">
                {simulateDialog && [0.5, 0.75, 0.9, 1].map((frac) => {
                  const remainingUsd = (simulateDialog.budget_usd || 0) - (simulateDialog.spent_usd || 0);
                  const remainingEur = usdToEur(remainingUsd);
                  const v = (remainingEur * frac).toFixed(2);
                  const lbl = frac === 1 ? 'Agotar' : `${Math.round((1 - frac) * 100)}% libre`;
                  return (
                    <button
                      key={frac}
                      type="button"
                      onClick={() => setSimulateAmount(v)}
                      className="text-[10px] px-1 py-1 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900"
                      data-testid={`quick-sim-${frac}`}
                    >
                      {lbl}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs">Nota (opcional)</Label>
              <Textarea
                value={simulateNote}
                onChange={(e) => setSimulateNote(e.target.value)}
                placeholder="Probar UI con saldo bajo…"
                className="mt-1 min-h-[50px]"
                data-testid="simulate-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSimulateDialog(null)}>Cancelar</Button>
            <Button
              onClick={submitSimulation}
              disabled={simulating}
              className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-simulate-btn"
            >
              {simulating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
              Simular consumo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Top up dialog */}
      <Dialog open={!!topUpDialog} onOpenChange={(o) => !o && setTopUpDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="topup-dialog">
          <DialogHeader>
            <DialogTitle>Recargar saldo IA</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Libro: <strong>{topUpDialog?.title}</strong><br/>
              Saldo actual: <strong>{fmtEur(usdToEur((topUpDialog?.budget_usd || 0) - (topUpDialog?.spent_usd || 0)))}</strong> de {fmtEur(usdToEur(topUpDialog?.budget_usd || 0))}
            </p>
            <div>
              <Label className="text-xs">Importe a añadir (EUR) <span className="text-red-500">*</span></Label>
              <Input
                type="number"
                step="0.5"
                min="0.5"
                value={topUpAmount}
                onChange={(e) => setTopUpAmount(e.target.value)}
                placeholder="Ej.: 5"
                className="mt-1"
                data-testid="topup-amount"
              />
              {topUpAmount && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  ≈ ${eurToUsd(parseFloat(topUpAmount || 0)).toFixed(2)} (cambio aproximado)
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">Nota (opcional)</Label>
              <Textarea
                value={topUpNote}
                onChange={(e) => setTopUpNote(e.target.value)}
                placeholder="Motivo de la recarga…"
                className="mt-1 min-h-[60px]"
                data-testid="topup-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopUpDialog(null)}>Cancelar</Button>
            <Button
              onClick={submitTopUp}
              disabled={toppingUp}
              className="gap-2 bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="confirm-topup-btn"
            >
              {toppingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpCircle className="w-4 h-4" />}
              Confirmar recarga
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print providers CRUD */}
      <PrintProvidersDialog
        open={providersDialogOpen}
        onClose={() => setProvidersDialogOpen(false)}
        onChanged={fetchOverview}
      />

      {/* Shipping providers CRUD (carriers like Correos, SEUR…) */}
      <ShippingProvidersDialog
        open={shippingProvidersDialogOpen}
        onClose={() => setShippingProvidersDialogOpen(false)}
        onChanged={fetchOverview}
      />

      {/* Project cost breakdown dialog (AI + print + shipping + margin) */}
      <Dialog
        open={!!projectCostDialog}
        onOpenChange={(o) => { if (!o) { setProjectCostDialog(null); setProjectBreakdown(null); } }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="project-cost-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-teal-600" />
              Coste del proyecto
            </DialogTitle>
            <DialogDescription className="text-xs">
              {projectCostDialog?.title}
            </DialogDescription>
          </DialogHeader>

          {providers.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground space-y-3">
              <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto" />
              <p>Aún no hay ningún proveedor de imprenta configurado.</p>
              <Button onClick={() => { setProjectCostDialog(null); setProvidersDialogOpen(true); }}>
                Crear el primer proveedor
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Controls */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-muted/30 rounded-lg">
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <Truck className="w-3 h-3" /> Proveedor de imprenta
                  </Label>
                  <Select
                    value={projectProviderId}
                    onValueChange={(v) => setProjectProviderId(v)}
                  >
                    <SelectTrigger className="mt-1 h-9" data-testid="project-provider-select">
                      <SelectValue placeholder="Selecciona…" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.filter((p) => p.is_active).map((p) => (
                        <SelectItem key={p.provider_id} value={p.provider_id}>
                          {p.name}{p.is_default ? ' · por defecto' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Cantidad de ejemplares</Label>
                  <Input
                    type="number" min={1} max={1000}
                    value={projectQty}
                    onChange={(e) => setProjectQty(Math.max(1, parseInt(e.target.value || '1', 10)))}
                    className="mt-1 h-9"
                    data-testid="project-qty-input"
                  />
                </div>
              </div>

              {/* Breakdown */}
              {projectLoading ? (
                <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
              ) : projectBreakdown ? (
                <>
                  {projectBreakdown.tariff_missing && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">No hay tarifa definida para {projectBreakdown.qty} ejemplares.</p>
                        <p className="mt-0.5">Añade una fila en el proveedor o elige una cantidad con tarifa.</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Our costs */}
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Nuestros costes</CardTitle></CardHeader>
                      <CardContent className="space-y-1.5 text-sm">
                        <Row label="IA (Claude / Gemini)" value={fmtEur(projectBreakdown.ai_spent_eur)} />
                        <Row label={`Impresión × ${projectBreakdown.qty}`} value={fmtEur(projectBreakdown.print_cost_eur)} />
                        <Row
                          label={
                            <span>
                              Envío al transportista ({fmtEur(projectBreakdown.shipping_real_eur || 0)})
                              {projectBreakdown.shipping_provider_name && (
                                <span className="ml-1 text-[10px] text-teal-700 font-medium" data-testid="cost-shipping-carrier">
                                  · {projectBreakdown.shipping_provider_name}
                                </span>
                              )}
                            </span>
                          }
                          value={
                            projectBreakdown.company_shipping_eur > 0
                              ? <span>{fmtEur(projectBreakdown.company_shipping_eur)} <span className="text-[10px] text-muted-foreground">(absorbemos)</span></span>
                              : <span className="text-muted-foreground">0,00 € <span className="text-[10px]">(cobrado al cliente)</span></span>
                          }
                        />
                        <hr className="my-1" />
                        <Row label="Coste total" value={fmtEur(projectBreakdown.our_total_cost_eur)} bold />
                      </CardContent>
                    </Card>

                    {/* Customer pays — driven by the tariff sheet directly */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Cliente paga (según tabla de precios)</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-1.5 text-sm">
                        {projectBreakdown.extra_price_eur !== null && projectBreakdown.qty > 1 ? (
                          <>
                            <Row label="Primer ejemplar" value="60,00 €" />
                            <Row
                              label={`Ejemplares extra × ${projectBreakdown.qty - 1} a ${fmtEur(projectBreakdown.extra_price_eur)}`}
                              value={fmtEur(projectBreakdown.extra_price_eur * (projectBreakdown.qty - 1))}
                            />
                            {projectBreakdown.volume_discount_pct > 0 && (
                              <p className="text-[10px] text-emerald-700 italic">
                                Descuento por volumen: −{projectBreakdown.volume_discount_pct}%
                              </p>
                            )}
                          </>
                        ) : (
                          <Row label="Ejemplar único" value="60,00 €" />
                        )}
                        <Row
                          label="Envío"
                          value={
                            projectBreakdown.customer_shipping_eur > 0
                              ? fmtEur(projectBreakdown.customer_shipping_eur)
                              : <span className="text-emerald-700 text-xs">gratis</span>
                          }
                        />
                        <hr className="my-1" />
                        <Row label="Ingreso total" value={fmtEur(projectBreakdown.actual_revenue_eur)} bold />
                        <div className="pt-1.5">
                          <Row
                            label="Margen neto"
                            value={
                              <span className={projectBreakdown.actual_net_margin_eur >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                                {fmtEur(projectBreakdown.actual_net_margin_eur)} ({projectBreakdown.actual_net_margin_pct.toFixed(1)}%)
                              </span>
                            }
                            bold
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Save provider preference for this book */}
                  <div className="flex items-center justify-between gap-3 p-3 border rounded-md text-xs">
                    <div className="text-muted-foreground">
                      {projectBreakdown.currently_assigned_provider_id === projectProviderId
                        ? '✓ Este proveedor ya está asignado al libro.'
                        : 'Asignar este proveedor al libro para que los cálculos futuros lo usen por defecto.'}
                    </div>
                    <Button
                      size="sm"
                      onClick={assignProviderToBook}
                      disabled={
                        assigningProvider ||
                        projectBreakdown.currently_assigned_provider_id === projectProviderId
                      }
                      className="gap-1"
                      data-testid="assign-provider-btn"
                    >
                      {assigningProvider ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Asignar al libro
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setProjectCostDialog(null); setProjectBreakdown(null); }}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Small inline row helper used in the cost breakdown cards. */
const Row = ({ label, value, bold = false }) => (
  <div className={`flex items-center justify-between ${bold ? 'font-semibold' : ''}`}>
    <span className="text-muted-foreground text-xs">{label}</span>
    <span>{value}</span>
  </div>
);
